/**
 * @file homeSectionOrder.ts
 * @brief The phone's Home order, applied to the web and desktop Home.
 *
 * @details
 * DES 11-10-2026: "mobile per jo ho wohi yahan per ho". The phone lets a user
 * drag Home's sections; it sends that order to `users.home_section_order`
 * (`homeSectionOrder` on `/auth/me`). The web has no drag of its own: it shows
 * its sections in the phone's order.
 *
 * The phone's list and its merge rule are copied from
 * `FRONTEND/src/screens/Home/homeSectionOrder.ts`; keep the two in step. The
 * web shows only the sections it has (`WEB_HOME_SECTIONS`); the phone-only
 * ones (glimpses, invoices, highlights) are skipped.
 *
 * @author DES
 * @date 11-10-2026
 * @updated 12-10-2026 — "todos" (My to-dos) after Quick Actions
 */

/** The phone's default order (FRONTEND homeSectionOrder.ts). "todos" = My
 *  to-dos, right after Quick Actions (DES 12-10-2026). */
const PHONE_DEFAULT_ORDER = [
  "glimpses",
  "quickActions",
  "todos",
  "scheduled",
  "meetings",
  "sessions",
  "notes",
  "invoices",
  "highlights",
] as const;

export const WEB_HOME_SECTIONS = [
  "quickActions",
  "todos",
  "scheduled",
  "meetings",
  "sessions",
  "notes",
] as const;

export type WebHomeSectionKey = (typeof WEB_HOME_SECTIONS)[number];

type PhoneKey = (typeof PHONE_DEFAULT_ORDER)[number];

function isPhoneKey(value: unknown): value is PhoneKey {
  return (PHONE_DEFAULT_ORDER as readonly unknown[]).includes(value);
}

function isWebKey(value: PhoneKey): value is WebHomeSectionKey {
  return (WEB_HOME_SECTIONS as readonly string[]).includes(value);
}

/**
 * A saved phone order → the web's sections in that order. Unknown keys and
 * duplicates are dropped; a section missing from the saved list goes where the
 * phone puts it (after its nearest default predecessor), as on the phone.
 */
export function resolveWebHomeOrder(
  stored: readonly unknown[] | null | undefined,
): WebHomeSectionKey[] {
  const order: PhoneKey[] = [];
  for (const key of stored ?? []) {
    if (isPhoneKey(key) && !order.includes(key)) order.push(key);
  }
  PHONE_DEFAULT_ORDER.forEach((key, defaultIndex) => {
    if (order.includes(key)) return;
    let insertAt = 0;
    for (let i = defaultIndex - 1; i >= 0; i--) {
      const at = order.indexOf(PHONE_DEFAULT_ORDER[i]);
      if (at !== -1) {
        insertAt = at + 1;
        break;
      }
    }
    order.splice(insertAt, 0, key);
  });
  return order.filter(isWebKey);
}
